import React from "react";
import { Box, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectChartCanvasSettings } from "../../selectors/chart";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { CanvasSettings as CanvasSettingsType, GridVisibility } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import { ChartLineStyle, LineThickness } from "../colorSelector/lineStyle.types";
import { defaultCanvasSettings } from "../../util/canvasSettingsDefaults";
import { chartSettingsRowSx, chartSettingsSectionDividerSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";

const FONT_SIZE_OPTIONS = [8, 10, 11, 12, 13, 14, 16];

type Props = {
    viewKey: string;
};

const {
    gridHorzColor: DEFAULT_HORZ_COLOR,
    gridHorzColorOpacity: DEFAULT_GRID_OPACITY,
    gridVertColor: DEFAULT_VERT_COLOR,
    crosshairColor: DEFAULT_CROSSHAIR_COLOR,
    crosshairColorOpacity: DEFAULT_CROSSHAIR_OPACITY,
    crosshairThickness: DEFAULT_CROSSHAIR_THICKNESS,
    crosshairLineStyle: DEFAULT_CROSSHAIR_STYLE,
    scaleTextColor: DEFAULT_SCALE_TEXT_COLOR,
    scaleTextColorOpacity: DEFAULT_SCALE_TEXT_OPACITY,
    scaleTextFontSize: DEFAULT_SCALE_TEXT_FONT_SIZE,
    scaleLinesColor: DEFAULT_SCALE_LINES_COLOR,
    scaleLinesColorOpacity: DEFAULT_SCALE_LINES_OPACITY,
} = defaultCanvasSettings;

const GRID_OPTIONS: { value: GridVisibility; label: string }[] = [
    { value: "both", label: "Both" },
    { value: "horizontal", label: "Horizontal Only" },
    { value: "vertical", label: "Vertical Only" },
    { value: "none", label: "None" },
];

const CanvasSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const canvasSettings = useSelector(selectChartCanvasSettings(viewKey));

    const gridVisibility: GridVisibility = canvasSettings?.gridVisibility ?? "both";
    const gridHorzColor = canvasSettings?.gridHorzColor ?? DEFAULT_HORZ_COLOR;
    const gridHorzColorOpacity = canvasSettings?.gridHorzColorOpacity ?? DEFAULT_GRID_OPACITY;
    const gridVertColor = canvasSettings?.gridVertColor ?? DEFAULT_VERT_COLOR;
    const gridVertColorOpacity = canvasSettings?.gridVertColorOpacity ?? DEFAULT_GRID_OPACITY;

    const crosshairColor = canvasSettings?.crosshairColor ?? DEFAULT_CROSSHAIR_COLOR;
    const crosshairOpacity = canvasSettings?.crosshairColorOpacity ?? DEFAULT_CROSSHAIR_OPACITY;
    const crosshairThickness: LineThickness = (canvasSettings?.crosshairThickness ?? DEFAULT_CROSSHAIR_THICKNESS) as LineThickness;
    const crosshairLineStyle: ChartLineStyle = (canvasSettings?.crosshairLineStyle ?? DEFAULT_CROSSHAIR_STYLE) as ChartLineStyle;

    const scaleTextColor = canvasSettings?.scaleTextColor ?? DEFAULT_SCALE_TEXT_COLOR;
    const scaleTextOpacity = canvasSettings?.scaleTextColorOpacity ?? DEFAULT_SCALE_TEXT_OPACITY;
    const scaleTextFontSize = canvasSettings?.scaleTextFontSize ?? DEFAULT_SCALE_TEXT_FONT_SIZE;
    const scaleLinesColor = canvasSettings?.scaleLinesColor ?? DEFAULT_SCALE_LINES_COLOR;
    const scaleLinesOpacity = canvasSettings?.scaleLinesColorOpacity ?? DEFAULT_SCALE_LINES_OPACITY;

    const updateSettings = (partial: Partial<CanvasSettingsType>) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                canvasSettings: {
                    ...canvasSettings,
                    ...partial,
                }
            },
            createIfAbsent: false,
        }));
    };

    const showHorzColor = gridVisibility === "both" || gridVisibility === "horizontal";
    const showVertColor = gridVisibility === "both" || gridVisibility === "vertical";

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                CHART BASIC STYLES
            </Typography>

            {/* Grid Lines */}
            <Box sx={chartSettingsRowSx}>
                <Typography fontSize="14px" sx={{ flexShrink: 0, minWidth: 80 }}>Grid Lines</Typography>
                <Select
                    size="small"
                    value={gridVisibility}
                    onChange={(e: SelectChangeEvent) =>
                        updateSettings({ gridVisibility: e.target.value as GridVisibility })
                    }
                    sx={{ fontSize: "13px", height: 32, minWidth: 130 }}
                >
                    {GRID_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} dense>
                            <Typography fontSize="13px">{opt.label}</Typography>
                        </MenuItem>
                    ))}
                </Select>
                {showHorzColor && (
                    <ColorSelector
                        compact
                        color={gridHorzColor}
                        opacity={gridHorzColorOpacity}
                        showOnlySwatch
                        onColorChange={(color, opacity) =>
                            updateSettings({ gridHorzColor: color, gridHorzColorOpacity: opacity })
                        }
                    />
                )}
                {showVertColor && (
                    <ColorSelector
                        compact
                        color={gridVertColor}
                        opacity={gridVertColorOpacity}
                        showOnlySwatch
                        onColorChange={(color, opacity) =>
                            updateSettings({ gridVertColor: color, gridVertColorOpacity: opacity })
                        }
                    />
                )}
            </Box>

            {/* Crosshair */}
            <Box sx={chartSettingsRowSx}>
                <Typography fontSize="14px" sx={{ flexShrink: 0, minWidth: 80 }}>Crosshair</Typography>
                <ColorSelector
                    compact
                    showOnlySwatch
                    color={crosshairColor}
                    opacity={crosshairOpacity}
                    lineThickness={crosshairThickness}
                    lineStyle={crosshairLineStyle}
                    onColorChange={(color, opacity) =>
                        updateSettings({ crosshairColor: color, crosshairColorOpacity: opacity })
                    }
                    onLineThicknessChange={(thickness) =>
                        updateSettings({ crosshairThickness: thickness })
                    }
                    onLineStyleChange={(style) =>
                        updateSettings({ crosshairLineStyle: style })
                    }
                />
            </Box>

            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}>
                SCALES
            </Typography>

            {/* Scale Text */}
            <Box sx={chartSettingsRowSx}>
                <Typography fontSize="14px" sx={{ flexShrink: 0, minWidth: 80 }}>Text</Typography>
                <ColorSelector
                    compact
                    showOnlySwatch
                    color={scaleTextColor}
                    opacity={scaleTextOpacity}
                    onColorChange={(color, opacity) =>
                        updateSettings({ scaleTextColor: color, scaleTextColorOpacity: opacity })
                    }
                />
                <Select
                    size="small"
                    value={scaleTextFontSize}
                    onChange={(e: SelectChangeEvent<number>) =>
                        updateSettings({ scaleTextFontSize: e.target.value as number })
                    }
                    sx={{ fontSize: "13px", height: 32, minWidth: 72 }}
                >
                    {FONT_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size} dense>
                            <Typography fontSize="13px">{size}</Typography>
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            {/* Scale Lines */}
            <Box sx={chartSettingsRowSx}>
                <Typography fontSize="14px" sx={{ flexShrink: 0, minWidth: 80 }}>Lines</Typography>
                <ColorSelector
                    compact
                    showOnlySwatch
                    color={scaleLinesColor}
                    opacity={scaleLinesOpacity}
                    onColorChange={(color, opacity) =>
                        updateSettings({ scaleLinesColor: color, scaleLinesColorOpacity: opacity })
                    }
                />
            </Box>
        </Box>
    );
};

export default CanvasSettings;
